import type { ManifestAsset } from '@tbcast/shared';

export const ASSET_CACHE = 'tbcast-assets-v1';
const PREFIX = '/__asset/';

export function assetPath(a: Pick<ManifestAsset, 'id' | 'checksum'>): string {
  return `${PREFIX}${encodeURIComponent(a.id)}?c=${encodeURIComponent(a.checksum.slice(0, 32))}`;
}

export interface SyncProgress {
  total: number;
  done: number;
  failed: number;
  bytes: number;
}

/**
 * Keeps the asset cache in sync with the manifest: downloads what is missing
 * (2 at a time, retries), removes what is no longer referenced. Delta sync:
 * unchanged assets (same id + checksum) are never downloaded again.
 */
export class AssetCache {
  private ready = new Set<string>();
  private urls = new Map<string, string>();
  private swActive = false;
  private syncing: Promise<SyncProgress> | null = null;
  onProgress: ((p: SyncProgress) => void) | null = null;

  constructor(private readonly canCache: boolean) {}

  setServiceWorkerActive(active: boolean): void {
    this.swActive = active;
  }

  /** URL to use for an asset: the cached copy when available, else the network URL. */
  url(assetId: string): string | null {
    const net = this.urls.get(assetId);
    const cachedPath = [...this.ready].find((p) =>
      p.startsWith(`${PREFIX}${encodeURIComponent(assetId)}?`),
    );
    if (cachedPath && this.swActive) return cachedPath;
    return net ?? null;
  }

  isCached(assetId: string): boolean {
    return [...this.ready].some((p) => p.startsWith(`${PREFIX}${encodeURIComponent(assetId)}?`));
  }

  async sync(assets: ManifestAsset[]): Promise<SyncProgress> {
    for (const a of assets) this.urls.set(a.id, a.url);
    if (!this.canCache || typeof caches === 'undefined')
      return { total: assets.length, done: 0, failed: 0, bytes: 0 };
    if (this.syncing) await this.syncing.catch(() => undefined);
    this.syncing = this.doSync(assets);
    try {
      return await this.syncing;
    } finally {
      this.syncing = null;
    }
  }

  private async doSync(assets: ManifestAsset[]): Promise<SyncProgress> {
    const cache = await caches.open(ASSET_CACHE);
    const wanted = new Map(assets.map((a) => [assetPath(a), a]));
    const existing = await cache.keys();
    const have = new Set<string>();
    for (const req of existing) {
      const u = new URL(req.url);
      const p = `${u.pathname}${u.search}`;
      if (wanted.has(p)) have.add(p);
      else await cache.delete(req);
    }
    this.ready = new Set([...this.ready].filter((p) => wanted.has(p) && have.has(p)));
    for (const p of have) this.ready.add(p);

    const todo = [...wanted.entries()].filter(([p]) => !have.has(p));
    const progress: SyncProgress = { total: wanted.size, done: have.size, failed: 0, bytes: 0 };
    this.onProgress?.(progress);
    let cursor = 0;
    const worker = async () => {
      while (cursor < todo.length) {
        const [path, asset] = todo[cursor++]!;
        const ok = await this.download(cache, path, asset);
        if (ok) {
          this.ready.add(path);
          progress.done++;
          progress.bytes += asset.sizeBytes;
        } else progress.failed++;
        this.onProgress?.(progress);
      }
    };
    await Promise.all([worker(), worker()]);
    return progress;
  }

  private async download(cache: Cache, path: string, asset: ManifestAsset): Promise<boolean> {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await fetch(asset.url, {
          cache: 'no-store',
          signal: AbortSignal.timeout(30 * 60_000),
        });
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        if (asset.sizeBytes && blob.size !== asset.sizeBytes) throw new Error('size mismatch');
        await cache.put(
          new Request(path),
          new Response(blob, {
            headers: {
              'Content-Type': asset.mimeType,
              'Content-Length': String(blob.size),
              'X-Asset-Id': asset.id,
            },
          }),
        );
        return true;
      } catch {
        await new Promise((r) => setTimeout(r, 2000 * (attempt + 1)));
      }
    }
    return false;
  }

  async clear(): Promise<void> {
    this.ready.clear();
    if (typeof caches !== 'undefined') await caches.delete(ASSET_CACHE);
  }
}

/** Remaining storage quota (for telemetry). */
export async function freeStorageBytes(): Promise<number | undefined> {
  try {
    const est = await navigator.storage?.estimate?.();
    if (est?.quota != null && est.usage != null) return est.quota - est.usage;
  } catch {
    // unsupported
  }
  return undefined;
}

export async function requestPersistentStorage(): Promise<void> {
  try {
    await navigator.storage?.persist?.();
  } catch {
    // unsupported
  }
}
